// Visitor counter.
//
// The Google Sheet is the only number the public footer shows.
// Tab "Counter", cell A2 is the count. Cell B2 is YES after the first public
// Launch website countdown. The website reads it with action=get on every page
// load. It does not read the Render visit total, and it does not keep the total in
// localStorage. localStorage only remembers whether THIS browser already counted
// today (apmercl_visitor_day).
//
// Before B2 is YES, action=hit is never sent. After that, a browser that has not
// counted today sends action=hit, then reads action=get again so a hand edit of
// A2 is what the footer shows. The same browser later the same day only reads.

import { envUrl } from "./env";
import { appsScriptJsonp, fetchJson } from "./jsonp";

const VISIT_DAY_KEY = "apmercl_visitor_day";
const STALE_TOTAL_KEY = "apmercl_shared_count";
const TRANSPORT_KEY = "apmercl_counter_transport";
const REQUEST_TIMEOUT_MS = 8000;
const BEGIN_TIMEOUT_MS = 20000;

export const VISITOR_REFRESH_EVENT = "apmercl-visitors-refresh";
export const COUNTER_URL = envUrl(process.env.REACT_APP_VISITOR_COUNTER_URL);

let countedThisPageLoad = false;

const readStore = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
};

function todayKey() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function isLikelyBot() {
  if (typeof navigator === "undefined") return false;
  return /bot|crawler|spider|preview|headless|slurp|facebookexternalhit/i.test(
    navigator.userAgent || ""
  );
}

export function alreadyCountedToday() {
  return readStore(VISIT_DAY_KEY) === todayKey();
}

export function markVisitorCountedToday() {
  writeStore(VISIT_DAY_KEY, todayKey());
}

export function clearVisitorCountedToday() {
  try {
    window.localStorage.removeItem(VISIT_DAY_KEY);
  } catch {
    /* private mode */
  }
}

function forgetStoredTotal() {
  try {
    window.localStorage.removeItem(STALE_TOTAL_KEY);
  } catch {
    /* private mode */
  }
}

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

/** Sheet payload -> the number to paint. Null when the reply is not a count. */
export function interpretSheet(data) {
  if (!data || typeof data !== "object" || data.ok === false) return null;
  const n = toCount(data.count);
  if (n === null) return null;
  return {
    count: n,
    authoritative: true,
    connected: true,
    launched: data.launched === true,
  };
}

/**
 * Add a visit only after the sheet says it has been launched, and only when
 * this browser has not already counted today. The check is made at hit time,
 * not when the page started, so the launch click can mark the day first.
 */
export function shouldIncrementSheet(data, alreadyCounted) {
  return !!(data && data.ok !== false && data.launched === true && alreadyCounted !== true);
}

function startNewVisit() {
  if (countedThisPageLoad) return false;
  countedThisPageLoad = true;
  if (isLikelyBot()) return false;
  if (alreadyCountedToday()) return false;
  return true;
}

function commitVisitDay() {
  markVisitorCountedToday();
}

async function readAppsScript(action, timeoutMs = REQUEST_TIMEOUT_MS) {
  const url = `${COUNTER_URL}?action=${encodeURIComponent(action)}&t=${Date.now()}`;
  let data;
  if (readStore(TRANSPORT_KEY) === "jsonp") {
    data = await appsScriptJsonp(url, timeoutMs);
  } else {
    try {
      data = await fetchJson(url, { method: "GET", redirect: "follow", cache: "no-store" }, timeoutMs);
      writeStore(TRANSPORT_KEY, "fetch");
    } catch {
      data = await appsScriptJsonp(url, timeoutMs);
      writeStore(TRANSPORT_KEY, "jsonp");
    }
  }
  return data && typeof data === "object" ? data : null;
}

const disconnected = () => ({ count: null, authoritative: false, connected: false, launched: false });

export async function resolveVisitorCount() {
  forgetStoredTotal();
  if (!COUNTER_URL) return disconnected();

  const mayCount = startNewVisit();
  let data;
  try {
    data = await readAppsScript("get");
  } catch {
    return disconnected();
  }
  let view = interpretSheet(data);
  if (!view) return disconnected();

  if (mayCount && shouldIncrementSheet(data, alreadyCountedToday())) {
    try {
      const hit = await readAppsScript("hit");
      if (hit && hit.counted === true) commitVisitDay();
      try {
        const fresh = await readAppsScript("get");
        const again = interpretSheet(fresh);
        if (again) return again;
      } catch {
        const fromHit = interpretSheet(hit);
        if (fromHit) return fromHit;
      }
    } catch {
      /* the get above is still the sheet number */
    }
  }
  return view;
}

/** Whether B2 is already YES. Unknown replies are not treated as launched. */
export async function sheetLaunchState() {
  if (!COUNTER_URL) return { known: false, launched: false };
  try {
    const data = await readAppsScript("get", 5000);
    if (!data || data.ok === false) return { known: false, launched: false };
    if (data.launched === true) return { known: true, launched: true };
    if (data.launched === false) return { known: true, launched: false };
    return { known: false, launched: false };
  } catch {
    return { known: false, launched: false };
  }
}

/** Public one-time start. No secret. Null when the script does not answer. */
export async function beginVisitorCount() {
  if (!COUNTER_URL) return null;
  try {
    return await readAppsScript("begin", BEGIN_TIMEOUT_MS);
  } catch {
    return null;
  }
}

export function notifyVisitorRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(VISITOR_REFRESH_EVENT));
}
